<template>
  <v-dialog v-model="dialog" persistent max-width="540px">
    <v-card>
      <v-card-title class="d-flex align-center py-4">
        <v-icon left color="primary">mdi-content-copy</v-icon>
        Clonar Dados para Conta Individual
      </v-card-title>

      <v-divider />

      <!-- ── CONFIGURAÇÃO ─────────────────────────────────── -->
      <template v-if="step === 'config'">
        <v-card-text class="pt-5">
          <v-alert type="info" text dense class="mb-5" icon="mdi-information-outline">
            Migra pacientes, sessões e transações de um profissional para uma conta individual.
            Os dados originais <strong>não são removidos</strong>.
          </v-alert>

          <!-- ORIGEM -->
          <div class="field-group-label mb-2">Origem</div>

          <v-text-field :value="sourceLabel" label="Clínica" outlined dense readonly
            prepend-inner-icon="mdi-hospital-building" class="mb-2" />

          <v-select v-model="form.source_user_id" :items="professionals" item-text="name" item-value="id"
            label="Usuário de origem" outlined dense prepend-inner-icon="mdi-account"
            no-data-text="Nenhum profissional encontrado" clearable />

          <!-- DESTINO -->
          <div class="field-group-label mb-2 mt-2">Destino</div>

          <v-autocomplete v-model="form.target_company" :items="companyResults" :search-input.sync="companySearch"
            :loading="searchingCompanies" item-text="display" item-value="id" label="Empresa de destino" outlined dense
            prepend-inner-icon="mdi-domain" no-data-text="Digite para pesquisar..." placeholder="Pesquisar empresa..."
            return-object clearable @change="onTargetCompanyChange" @click:clear="onTargetCompanyClear" />

          <v-select v-model="form.target_user_id" :items="targetCompanyUsers" item-text="name" item-value="id"
            label="Usuário de destino" outlined dense prepend-inner-icon="mdi-account-circle"
            :disabled="!form.target_company" :loading="loadingTargetUsers"
            :no-data-text="!form.target_company ? 'Selecione uma empresa primeiro' : 'Nenhum usuário encontrado'"
            clearable />
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-btn text @click="close">Cancelar</v-btn>
          <v-spacer />
          <v-btn color="primary" :disabled="!canProceed" @click="step = 'confirm'">
            Próximo
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-actions>
      </template>

      <!-- ── CONFIRMAÇÃO ──────────────────────────────────── -->
      <template v-if="step === 'confirm'">
        <v-card-text class="pt-5">
          <v-alert type="warning" outlined dense class="mb-5">
            <div class="font-weight-bold mb-1">Atenção — leia antes de continuar</div>
            Esta operação pode levar vários minutos. Pacientes, sessões e transações
            serão copiados para a conta destino.
          </v-alert>

          <v-list dense class="rounded outlined-list">
            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-hospital-building</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>Clínica de origem</v-list-item-subtitle>
                <v-list-item-title>{{ sourceLabel }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-divider inset />

            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-account</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>Usuário de origem</v-list-item-subtitle>
                <v-list-item-title>{{ selectedSourceUserName }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-divider inset />

            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-domain</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>Empresa de destino</v-list-item-subtitle>
                <v-list-item-title>{{ form.target_company && form.target_company.display }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-divider inset />

            <v-list-item>
              <v-list-item-icon><v-icon color="primary">mdi-account-circle</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>Usuário de destino</v-list-item-subtitle>
                <v-list-item-title>{{ selectedTargetUserName }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-btn text @click="step = 'config'">
            <v-icon left>mdi-arrow-left</v-icon>
            Voltar
          </v-btn>
          <v-spacer />
          <v-btn color="warning" dark @click="startClone" :loading="starting">
            <v-icon left>mdi-play</v-icon>
            Iniciar Migração
          </v-btn>
        </v-card-actions>
      </template>

      <!-- ── EM ANDAMENTO ─────────────────────────────────── -->
      <template v-if="step === 'running'">
        <v-card-text class="text-center pt-8 pb-6">
          <v-progress-circular :value="progress.percent" :size="80" :width="6" color="primary" class="mb-5">
            <span class="caption font-weight-bold">{{ progress.percent }}%</span>
          </v-progress-circular>

          <div class="text-h6 mb-1">Migração em andamento…</div>
          <div class="text--secondary mb-6">
            {{ progress.processed_customers }} de {{ progress.total_customers }} pacientes processados
          </div>

          <v-progress-linear :value="progress.percent" color="primary" height="6" rounded />
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn text small @click="close">Fechar (processo continua em background)</v-btn>
        </v-card-actions>
      </template>

      <!-- ── CONCLUÍDO ────────────────────────────────────── -->
      <template v-if="step === 'done'">
        <v-card-text class="text-center pt-8 pb-6">
          <v-icon size="64" color="success" class="mb-4">mdi-check-circle-outline</v-icon>
          <div class="text-h6 mb-2">Migração concluída!</div>
          <div class="text--secondary">Todos os dados foram migrados com sucesso.</div>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn color="success" @click="close">Fechar</v-btn>
        </v-card-actions>
      </template>

      <!-- ── ERRO ─────────────────────────────────────────── -->
      <template v-if="step === 'error'">
        <v-card-text class="text-center pt-8 pb-6">
          <v-icon size="64" color="error" class="mb-4">mdi-alert-circle-outline</v-icon>
          <div class="text-h6 mb-2">Erro na migração</div>
          <div class="text--secondary caption mt-1">{{ progress.error }}</div>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-btn text @click="step = 'config'">Tentar novamente</v-btn>
          <v-spacer />
          <v-btn text @click="close">Fechar</v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  data() {
    return {
      dialog: false,
      step: 'config',
      starting: false,
      company: null,

      // busca de empresa destino
      companySearch: '',
      companyResults: [],
      searchingCompanies: false,
      searchTimeout: null,

      // usuários da empresa destino
      targetCompanyUsers: [],
      loadingTargetUsers: false,

      form: {
        source_user_id: null,
        target_company: null,
        target_user_id: null,
      },

      progress: {
        status: null,
        total_customers: 0,
        processed_customers: 0,
        percent: 0,
        error: null,
      },

      pollInterval: null,
    }
  },

  computed: {
    professionals() {
      if (!this.company?.users) return []
      return this.company.users
        .filter(u => u.company_user?.is_professional)
        .map(u => ({ id: u.id, name: u.name, email: u.email }))
    },

    selectedSourceUserName() {
      return this.professionals.find(p => p.id === this.form.source_user_id)?.name ?? '—'
    },

    selectedTargetUserName() {
      return this.targetCompanyUsers.find(u => u.id === this.form.target_user_id)?.name ?? '—'
    },

    sourceLabel() {
      if (!this.company) return '—'
      return `${this.company.name} (#${this.company.id})`
    },

    canProceed() {
      return !!(this.form.source_user_id && this.form.target_company && this.form.target_user_id)
    },
  },

  watch: {
    companySearch(val) {
      if (this.form.target_company) return
      if (!val || val.length < 2) return
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => this.searchCompanies(val), 400)
    },
  },

  beforeDestroy() {
    this.stopPolling()
    clearTimeout(this.searchTimeout)
  },

  methods: {
    open(company) {
      this.company = company
      this.step = 'config'
      this.companyResults = []
      this.targetCompanyUsers = []
      this.form = { source_user_id: null, target_company: null, target_user_id: null }
      this.progress = { status: null, total_customers: 0, processed_customers: 0, percent: 0, error: null }
      this.dialog = true
    },

    close() {
      this.stopPolling()
      this.dialog = false
    },

    searchCompanies(company_id) {

      this.searchingCompanies = true
      this.$http
        .index('admin/companies', { company_id, company_type: ['individual'] })
        .then(response => {
          this.companyResults = (response.companies?.data ?? []).map(c => ({
            id: c.id,
            display: `${c.name} (#${c.id})`,
          }))
        })
        .catch(() => {
          this.companyResults = []
        })
        .finally(() => {
          this.searchingCompanies = false
        })
    },

    onTargetCompanyChange(company) {
      this.form.target_user_id = null
      this.targetCompanyUsers = []

      if (!company) return

      this.loadingTargetUsers = true
      this.$http
        .show('admin/companies', company.id)
        .then(response => {
          this.targetCompanyUsers = (response.company?.users ?? []).map(u => ({
            id: u.id,
            name: u.name,
          }))
        })
        .catch(() => {
          this.targetCompanyUsers = []
        })
        .finally(() => {
          this.loadingTargetUsers = false
        })
    },

    onTargetCompanyClear() {
      this.form.target_user_id = null
      this.targetCompanyUsers = []
    },

    startClone() {
      this.starting = true
      this.$http
        .store('admin/clone-from-clinic', {
          source_user_id: this.form.source_user_id,
          source_company_id: this.company.id,
          target_user_id: this.form.target_user_id,
          target_company_id: this.form.target_company.id,
        })
        .then(() => {
          this.step = 'running'
          this.startPolling()
        })
        .catch(() => {
          this.step = 'error'
        })
        .finally(() => {
          this.starting = false
        })
    },

    startPolling() {
      this.pollProgress()
      this.pollInterval = setInterval(this.pollProgress, 3000)
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
    },

    pollProgress() {
      this.$http
        .show('admin/clone-status', this.form.source_user_id)
        .then(response => {
          this.progress = response
          if (response.status === 'done') {
            this.step = 'done'
            this.stopPolling()
          } else if (response.status === 'failed') {
            this.step = 'error'
            this.stopPolling()
          }
        })
        .catch(() => { })
    },
  },
}
</script>

<style scoped>
.outlined-list {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.field-group-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(0, 0, 0, 0.45);
}
</style>
